import React, { useState, useEffect } from 'react';
import TrackingPage from './components/TrackingPage/TrackingPage';
import AnalysisPage from './components/AnalysisPage/AnalysisPage';
import SettingsPage from './components/SettingsPage/SettingsPage';
import Sidebar from './components/Sidebar/Sidebar';
import LoginPage from './components/Auth/LoginPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DatabaseProvider } from './context/DatabaseContext';
import { RugbyTrackerProvider } from './context/RugbyTrackerContext';
import { ThemeProvider } from './context/ThemeContext';

// Import CSS
import './styles/themes.css';
import './App.css';

// Import useToast hook for notifications
import useToast from './hooks/useToast';

function AppContent() {
    // Auth hook
    const { user, loading } = useAuth();

    // Initialize toast hook
    const { toast, showToast, showSuccessToast, showErrorToast, showWarningToast } = useToast();

    // State for sidebar and active page
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // Set collapsed by default
    const [activePage, setActivePage] = useState('tracking');

    // Toggle sidebar function
    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    // Effect to handle page changes
    useEffect(() => {
        // Close sidebar on mobile after selecting a page
        if (window.innerWidth <= 768) {
            setSidebarOpen(false);
        }
    }, [activePage]);

    // Show loading indicator while auth initializes
    if (loading) {
        return <div className="auth-loading">Loading...</div>;
    }

    // Show login page if not authenticated
    if (!user) {
        return <LoginPage />;
    }

    // Determine which page component to render based on activePage
    const renderActivePage = () => {
        switch (activePage) {
            case 'analysis':
                return (
                    <AnalysisPage
                        toggleSidebar={toggleSidebar}
                        showToast={showToast}
                    />
                );
            case 'settings':
                return (
                    <SettingsPage 
                        showToast={showToast}
                    />
                );
            case 'tracking':
            default:
                return (
                    <TrackingPage
                        showToast={showToast}
                        showSuccessToast={showSuccessToast}
                        showErrorToast={showErrorToast}
                        showWarningToast={showWarningToast}
                    />
                );
        }
    };

    return (
        <RugbyTrackerProvider>
            <div className={`app-container ${sidebarOpen ? 'sidebar-open' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                {/* Sidebar Component */}
                <Sidebar
                    isOpen={sidebarOpen}
                    toggleSidebar={toggleSidebar}
                    activePage={activePage}
                    setActivePage={setActivePage}
                    onCollapsedChange={setSidebarCollapsed}
                />

                {/* Main Content Area */}
                <div className="main-content">
                    {/* Render the active page component directly without header */}
                    {renderActivePage()}
                </div>

                {/* Toast Notification */}
                <div className={`toast ${toast.type} ${toast.show ? 'show' : ''}`}>
                    {toast.message}
                </div>
            </div>
        </RugbyTrackerProvider>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <DatabaseProvider>
                    <AppContent />
                </DatabaseProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;